import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import {
  FeedbackComment,
  FeedbackEntityType,
  FeedbackThreadState,
  deleteFeedback,
  editFeedback,
  getFeedback,
  postFeedback,
  setThreadResolved,
} from '../../api/feedbackApi';
import { positionDropdown } from '../../utils/dropdownPosition';

/**
 * Doc-style comments on a TEND entity. One component for every feedback
 * surface — the entity is addressed by entity_type + entity_key.
 *
 * Layouts:
 * - 'floating': fixed portal beside the anchor card. Follows the viewport
 *   as the page scrolls (read feedback while scrolling long details) but
 *   CLAMPS to the card's top/bottom — it rides off-screen with the card
 *   rather than detaching from it. Deliberately NO click-outside close —
 *   feedback is about what the card shows, so people click back and forth
 *   while typing. Escape, the ×, the chat icon, or opening feedback on
 *   another card closes it.
 * - 'inline': fills its parent column (the TendedItemModal side panel).
 * - 'anchored': Phase 2 surfaces (dropdown sections, modal headers, table
 *   rows) — opens at the trigger via the shared dropdownPosition clamp,
 *   flips at viewport edges, tracks its anchor through scrolls, and
 *   renders ABOVE dropdowns/modals (fixed spots, not traveling cards).
 * - 'sheet': phones. There is no "beside the card" on a narrow viewport,
 *   so the panel opens OVER the card as a bottom sheet under the frost
 *   veil, above the whole modal band (a deep-linked card sits inside the
 *   item modal at z 999999 — the floating panel's z 15 opened underneath
 *   it: chat icon lit, nothing appeared; Mobile Content Feedback ticket,
 *   item 5027). Tapping the veil closes (no Escape key on a phone). A
 *   'floating' request on a narrow viewport becomes this by itself.
 */

interface FeedbackPanelProps {
  entityType: FeedbackEntityType;
  entityKey: string;
  brandId: number;
  onClose: () => void;
  layout: 'floating' | 'inline' | 'anchored' | 'sheet';
  /** floating: the card element the panel hugs (right edge, 16px gap).
      anchored: the trigger element the panel opens against. */
  anchorEl?: HTMLElement | null;
  /** Shown in the footer copy-link affordance (new ?item= format). */
  shareUrl?: string;
  /** Count delta callback so badge dots stay honest without a refetch. */
  onCountChange?: (delta: number) => void;
  /** Resolve-state callback so icons flip filled/open without a refetch. */
  onThreadChange?: (resolved: boolean) => void;
}

const PANEL_WIDTH = 320;
const VIEWPORT_GAP = 16;
const COMPOSER_MAX_HEIGHT = 120;
const SHEET_MAX_WIDTH = 480;
// The same line TendedItemModal draws for its side column: below it the
// panel has no room beside a card.
const WIDE_VIEWPORT_QUERY = '(min-width: 1024px)';

/** Live "is there room beside a card" flag; true where matchMedia is
    unavailable (nothing changes for environments that can't say). */
const useWideViewport = (): boolean => {
  const canQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [wide, setWide] = useState<boolean>(() => (canQuery ? window.matchMedia(WIDE_VIEWPORT_QUERY).matches : true));
  useEffect(() => {
    if (!canQuery) return;
    const mq = window.matchMedia(WIDE_VIEWPORT_QUERY);
    const onChange = () => setWide(mq.matches);
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [canQuery]);
  return wide;
};

// One floating panel at a time, app-wide: opening feedback on another card
// closes this one (cleaner than click-outside, which would fight the
// type-while-looking-at-the-card workflow).
const OPEN_EVENT = 'tend-feedback-open';

const timeLabel = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/**
 * Is this element hosted inside the modal band — a body-portaled overlay at
 * an item modal's rung or above (TendedContentModal, TendedItemModal, the
 * editor modals)? Read from the ancestors' z-index: nothing on a plain page
 * sits that high, so the first ancestor at ≥ ITEM_MODAL means "in a modal".
 */
export function isInsideModalBand(el: HTMLElement | null): boolean {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const z = parseInt(node.style.zIndex || window.getComputedStyle(node).zIndex, 10);
    if (Number.isFinite(z) && z >= Z.ITEM_MODAL) return true;
    node = node.parentElement;
  }
  return false;
}

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
  entityType,
  entityKey,
  brandId,
  onClose,
  layout,
  anchorEl,
  shareUrl,
  onCountChange,
  onThreadChange,
}) => {
  const [comments, setComments] = useState<FeedbackComment[] | null>(null);
  const [thread, setThread] = useState<FeedbackThreadState>({ resolved: false });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const instanceRef = useRef<object>({});
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null);
  const wide = useWideViewport();
  // A floating panel has no "beside the card" on a phone: it is the sheet.
  const effectiveLayout: FeedbackPanelProps['layout'] = layout === 'floating' && !wide ? 'sheet' : layout;
  // A floating panel on a PAGE sits at z 15 so it tucks under the sticky
  // chrome as its card scrolls off. Inside a body-portaled modal that rung
  // is behind the modal itself — the chat icon lit up and nothing appeared
  // from the tended view of an original content card (2026-10-08; the phone
  // case had the same cause and became the sheet). The anchor's ancestry
  // says which world the card is in; in a modal the panel clears the band.
  const floatingZ = useMemo(() => (isInsideModalBand(anchorEl ?? null) ? Z.FEEDBACK : 15), [anchorEl]);
  // Sheet: track the VISUAL viewport so the sheet sits above the on-screen
  // keyboard (a fixed bottom edge hides behind it on iOS otherwise).
  const [visual, setVisual] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => {
    if (effectiveLayout !== 'sheet') return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setVisual({ top: vv.offsetTop, height: vv.height });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [effectiveLayout]);

  // Anchored placement: glued to the trigger via the shared clamp+flip.
  useEffect(() => {
    if (layout !== 'anchored' || !anchorEl) return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const anchor = anchorEl.getBoundingClientRect();
      const dp = positionDropdown(anchor, PANEL_WIDTH, 360, { align: 'center' });
      setPos({
        left: dp.left,
        top: dp.top ?? Math.max(VIEWPORT_GAP, window.innerHeight - (dp.bottom ?? 0) - (panelRef.current?.offsetHeight ?? 360)),
        maxHeight: dp.maxHeight,
      });
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(compute); };
    compute();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, anchorEl, comments?.length, draft]);

  // Floating placement: viewport-follow, clamped to the card's bounds.
  useEffect(() => {
    if (effectiveLayout !== 'floating' || !anchorEl) return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const card = anchorEl.getBoundingClientRect();
      const panelH = panelRef.current?.offsetHeight ?? 320;
      let left = card.right + VIEWPORT_GAP;
      if (left + PANEL_WIDTH + VIEWPORT_GAP > window.innerWidth) {
        left = Math.max(VIEWPORT_GAP, window.innerWidth - PANEL_WIDTH - VIEWPORT_GAP);
      }
      // Prefer hugging the top of the usable viewport — below ALL sticky
      // page chrome (brand-header pill + the Tended tabs cap), with a
      // breathing gap — while the card spans it; never above the card's
      // top edge, never past its bottom edge.
      let headerBottom = 0;
      document
        .querySelectorAll('.tend-page-header, .tend-pubtabs-sticky')
        .forEach((el) => {
          headerBottom = Math.max(headerBottom, el.getBoundingClientRect().bottom);
        });
      const followTop = Math.max(VIEWPORT_GAP, headerBottom + 12);
      const minTop = card.top;
      const maxTop = card.bottom - panelH;
      const top = Math.min(Math.max(followTop, minTop), Math.max(minTop, maxTop));
      setPos({
        left,
        top,
        maxHeight: Math.min(480, window.innerHeight - followTop - VIEWPORT_GAP),
      });
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(compute); };
    compute();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
    // Re-clamp when content height changes (messages arrive, composer grows).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLayout, anchorEl, comments?.length, draft]);

  // Single-open coordination across every floating/anchored panel.
  useEffect(() => {
    if (layout === 'inline') return;
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: instanceRef.current }));
    const onOther = (e: Event) => {
      if ((e as CustomEvent).detail !== instanceRef.current) onClose();
    };
    window.addEventListener(OPEN_EVENT, onOther);
    return () => window.removeEventListener(OPEN_EVENT, onOther);
  }, [layout, onClose]);

  // Composer grows downward with the draft (capped, then scrolls).
  const autoGrowComposer = useCallback(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, []);

  useEffect(() => {
    let alive = true;
    getFeedback(entityType, entityKey, brandId)
      .then(({ feedback, thread: t }) => {
        if (!alive) return;
        setComments(feedback);
        setThread(t);
      })
      .catch(() => { if (alive) setComments([]); });
    return () => { alive = false; };
  }, [entityType, entityKey, brandId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // New messages scroll into view.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments?.length]);

  const send = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const created = await postFeedback(entityType, entityKey, brandId, body);
      setComments((prev) => [...(prev || []), created]);
      setDraft('');
      onCountChange?.(1);
      // Server auto-reopens on new comments — mirror locally.
      if (thread.resolved) {
        setThread({ resolved: false });
        onThreadChange?.(false);
      }
      requestAnimationFrame(() => {
        const el = composerRef.current;
        if (el) el.style.height = 'auto';
      });
    } catch {
      // keep the draft; the user can retry
    } finally {
      setSending(false);
    }
  }, [draft, sending, entityType, entityKey, brandId, onCountChange]);

  const remove = useCallback(async (id: number) => {
    const ok = await deleteFeedback(id);
    if (ok) {
      setComments((prev) => (prev || []).filter((c) => c.id !== id));
      onCountChange?.(-1);
    }
  }, [onCountChange]);

  const toggleResolved = useCallback(async () => {
    const next = !thread.resolved;
    try {
      const t = await setThreadResolved(entityType, entityKey, brandId, next);
      setThread(t);
      onThreadChange?.(t.resolved);
    } catch { /* leave state as-is */ }
  }, [thread.resolved, entityType, entityKey, brandId, onThreadChange]);

  const saveEdit = useCallback(async (id: number) => {
    const body = editDraft.trim();
    if (!body) return;
    try {
      const updated = await editFeedback(id, body);
      setComments((prev) => (prev || []).map((c) => (c.id === id ? updated : c)));
      setEditingId(null);
    } catch { /* keep editor open for retry */ }
  }, [editDraft]);

  const copyLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  }, [shareUrl]);

  const panel = (
    <div
      ref={panelRef}
      className="tend-card-chrome flex flex-col rounded-xl border border-white/15 shadow-2xl overflow-hidden"
      style={{
        width: effectiveLayout === 'inline' || effectiveLayout === 'sheet' ? '100%' : PANEL_WIDTH,
        maxHeight: effectiveLayout === 'inline'
          ? '100%'
          : effectiveLayout === 'sheet'
            ? Math.round((visual?.height ?? window.innerHeight) * 0.7)
            : pos?.maxHeight ?? 480,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white/10 flex-shrink-0">
        <span className="tend-micro">Feedback</span>
        <span className="flex items-center gap-1">
        <button
          type="button"
          onClick={toggleResolved}
          className={`p-1 rounded transition-colors ${
            thread.resolved ? 'text-white' : 'text-white/50 hover:text-white'
          }`}
          title={thread.resolved ? 'Reopen thread' : 'Mark resolved'}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded text-white/50 hover:text-white transition-colors"
          title="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        </span>
      </div>

      {/* Resolved strip — quiet, with the reopen affordance */}
      {thread.resolved && (
        <div className="px-4 py-1.5 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <span className="text-[11px] text-white/50">
            Resolved{thread.resolved_by ? ` by ${thread.resolved_by}` : ''}
          </span>
          <button
            type="button"
            onClick={toggleResolved}
            className="text-[11px] text-white/50 hover:text-white/80 transition-colors"
          >
            Reopen
          </button>
        </div>
      )}

      {/* Messages — an empty thread shows just the composer, no null-state copy */}
      <div
        ref={listRef}
        className={`flex-1 min-h-0 overflow-y-auto px-4 space-y-3 ${
          comments && comments.length === 0 ? 'py-0' : 'py-3'
        }`}
      >
        {comments === null ? (
          <div className="tend-micro" style={{ textTransform: 'none', opacity: 0.6 }}>Loading…</div>
        ) : (
          comments.map((c) => (
            <div key={c.id} className="group flex gap-2.5">
              <span
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white"
                style={{ background: 'rgba(255,255,255,.14)' }}
              >
                {c.author.initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xs font-medium text-white truncate">{c.author.name}</span>
                  <span className="text-[10px] text-white/45 flex-shrink-0">({c.author.badge})</span>
                  <span className="text-[10px] text-white/30 flex-shrink-0 ml-auto">{timeLabel(c.created_at)}</span>
                  {c.is_own && editingId !== c.id && (
                    <button
                      type="button"
                      onClick={() => { setEditingId(c.id); setEditDraft(c.body); }}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-white/35 hover:text-white/80 transition-all"
                      title="Edit comment"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                  {/* Hidden while editing — the × reads as "close editor"
                      and would delete the comment instead */}
                  {c.is_own && editingId !== c.id && (
                    <button
                      type="button"
                      onClick={() => remove(c.id)}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-white/35 hover:text-red-400 transition-all"
                      title="Delete comment"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {editingId === c.id ? (
                  <div className="flex items-end gap-1.5 mt-0.5">
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(c.id); }
                        if (e.key === 'Escape') { e.stopPropagation(); setEditingId(null); }
                      }}
                      rows={2}
                      autoFocus
                      className="tend-ink-field flex-1 resize-none text-[13px]"
                    />
                    <button
                      type="button"
                      onClick={() => saveEdit(c.id)}
                      className="text-[11px] text-white/60 hover:text-white pb-1"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <div className="text-[13px] text-white/85 whitespace-pre-wrap break-words leading-snug mt-0.5">
                    {c.body}
                    {c.edited_at && <span className="text-[10px] text-white/30 ml-1">(edited)</span>}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Composer */}
      <div className="px-3 pb-2 pt-1 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={composerRef}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); autoGrowComposer(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Leave feedback…"
            rows={1}
            className="tend-ink-field flex-1 resize-none text-[13px]"
            style={{ minHeight: 36, maxHeight: COMPOSER_MAX_HEIGHT, overflowY: 'auto' }}
          />
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim() || sending}
            className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
              draft.trim() && !sending
                ? 'text-white bg-blue-600 hover:bg-blue-700'
                : 'text-white/30 bg-white/10 cursor-default'
            }`}
            title="Send"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a.993.993 0 0 0-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Footer — the share link's new home */}
      {shareUrl && (
        <div className="px-4 py-2 border-t border-white/10 flex-shrink-0">
          <button
            type="button"
            onClick={copyLink}
            className="flex items-center gap-1.5 text-[11px] text-white/45 hover:text-white/80 transition-colors"
            title={shareUrl}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {linkCopied ? 'Link copied' : 'Copy link to this post'}
          </button>
        </div>
      )}
    </div>
  );

  if (effectiveLayout === 'inline') return panel;

  if (effectiveLayout === 'sheet') {
    return createPortal(
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          left: 0,
          top: visual?.top ?? 0,
          width: '100%',
          height: visual?.height ?? '100%',
          // Above the whole modal band (an item modal is 999999).
          zIndex: Z.FEEDBACK,
          // The sheet veil: tend.css .tend-veil's frost restated inline —
          // that class hides itself ≥1024px and rides the profile sheet's
          // clock; the numbers are the same.
          background: 'rgba(255, 255, 255, 0.04)',
          WebkitBackdropFilter: 'blur(10px)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          padding: '0 12px calc(12px + env(safe-area-inset-bottom))',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{ width: '100%', maxWidth: SHEET_MAX_WIDTH, display: 'flex' }}
          onClick={(e) => e.stopPropagation()}
        >
          {panel}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        // floating: under both sticky headers (.tend-page-header z50,
        // .tend-pubtabs-sticky z20) so scrolling off with the card tucks
        // BEHIND the chrome; cards isolate their internal z layers (root
        // `isolate`) so the panel still paints over neighboring cards.
        // anchored: fixed spots inside dropdowns/modals — must render ABOVE
        // their hosts. Modals here stack into the 1_000_000+ range (e.g. the
        // tactic modal at 1000003), so 9800 rendered the panel BEHIND them
        // (feedback "didn't open"). Sit above the whole modal layer.
        zIndex: layout === 'anchored' ? Z.FEEDBACK : floatingZ,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {panel}
    </div>,
    document.body
  );
};

export default FeedbackPanel;
